/* Native collection URLs remain the fallback for every enhanced action. */
import { lockScroll, unlockScroll } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

// Milen's IX2 easing "outQuad" is exactly p * (2 - p), which as a cubic Bezier is
// (1/3, 2/3, 2/3, 1). His CSS transitions use the approximation cubic-bezier(.25,.46,.45,.94);
// IX2 tweens (card hover, sheet) follow the exact curve, as his measured frames do.
const OUT_QUAD = 'cubic-bezier(0.3333333,0.6666667,0.6666667,1)';
// IX2 "inQuad" is exactly p * p: cubic Bezier (1/3, 0, 2/3, 1/3).
const IN_QUAD = 'cubic-bezier(0.3333333,0,0.6666667,0.3333333)';
// One 60Hz frame, and how long a tap handler may run before the sheet's first frame counts as late.
const FRAME = 1000 / 60;
const LATE = 3 * FRAME;
// IX2 starts an action at its last frame tick before the triggering event, so its first painted
// frame already shows one frame of progress. While the pointer is over the listing the shared
// IX2 frame clock (xz-v2-frame-clock.js) runs and gives that same tick; otherwise the event's own
// timestamp is used. Either is never later than the frame that renders it, so no tween runs
// backwards.
function eventStart(event) {
  const now = performance.now();
  const at = event && Number.isFinite(event.timeStamp) ? event.timeStamp : now;
  return at <= now && now - at < 100 ? at : now;
}

class XinzuoV2Collection extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.dataset.plpEnhanced = 'true';
    this.sheet = this.querySelector('[data-plp-sheet]');
    this.sheetParts = ['.xz-filter-wrp-mobile', '.xz-filter-buttons-wrp', '.xz-close-btn']
      .map((selector) => this.sheet?.querySelector(selector)).filter(Boolean);
    if (!this.sheet?.open) this.resetSheet();
    this.cardMotion = new Map();
    if (!this.pageStart) {
      this.pageStart = Number(this.dataset.currentPage) || 1;
      this.restoreOnLoad();
    }
    this.updatePrev();
    // Scroll position is kept per history entry, so Back from a product lands where the shopper
    // left, after the loaded range has been replayed (the browser cannot restore it itself).
    window.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.saveScroll(), 200);
    }, { passive: true, signal });
    window.addEventListener('pagehide', () => this.saveScroll(), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('pointerenter', () => this.holdClock(true), { signal });
    this.addEventListener('pointerleave', () => this.holdClock(false), { signal });
    for (const type of ['pointerover', 'focusin', 'touchstart']) this.addEventListener(type, (event) => {
      const link = event.target.closest?.('[data-plp-load-more]');
      if (link && this.contains(link)) this.prefetchMore(link, true);
    }, { signal, passive: true });
    this.watchLoadMore();
    this.addEventListener('keydown', (event) => {
      const radio = event.target.closest?.('[data-plp-form="desktop"] input[type="radio"]');
      if (!radio) return;
      if (/^Arrow/.test(event.key)) this.arrowKey = true;
      else if ((event.key === ' ' || event.key === 'Enter') && this.pendingForm) {
        event.preventDefault();
        this.flushPending(radio.id);
      }
    }, { signal });
    // The flag spans keydown to keyup, so the change event an arrow key causes is always seen.
    this.addEventListener('keyup', (event) => { if (/^Arrow/.test(event.key)) setTimeout(() => { this.arrowKey = false; }, 0); }, { signal });
    this.addEventListener('pointerdown', () => { this.arrowKey = false; }, { signal });
    // A click on a filter row ticks it where it is. The label focuses its box with the browser's
    // scroll-into-view, which honours the header's scroll-padding (128px) and the sheet's (104px): a row
    // clicked near an edge jumped away under the pointer before anything was applied (filtering-r3-1).
    // The scroll positions at the click are put back as soon as the box reports the change (or in the
    // next frame), before anything is painted. The box keeps the browser's own mouse focus (no focus
    // ring), and keyboard focus still scrolls into view.
    this.addEventListener('click', (event) => {
      const label = event.target.closest?.('[data-plp-form] label');
      if (!label || !this.contains(label) || event.target.closest('input')) return;
      const scroller = label.closest('.xz-filter-wrp-mobile');
      const press = this.pressScroll = { y: window.scrollY, scroller, top: scroller ? scroller.scrollTop : 0 };
      requestAnimationFrame(() => { if (this.pressScroll === press) this.keepPressScroll(); });
    }, { signal, capture: true });
    this.addEventListener('focusout', (event) => {
      // Chrome fires focusout on the focused input while replaceWith() detaches this listing;
      // that is not the shopper leaving the field, and navigate() is already running.
      if (this.swapping || !this.isConnected) return;
      // A sheet refresh held back while the shopper was typing a price runs once focus leaves the fields.
      const sheetPrice = this.stageDeferred && event.target.closest?.('[data-plp-form="mobile"] .xz-v2-collection-price');
      if (sheetPrice && !sheetPrice.contains(event.relatedTarget)) this.stageSheet(sheetPrice.closest('form'));
      const form = event.target.closest?.('[data-plp-form="desktop"]');
      if (!form || !this.contains(form)) return;
      const group = event.target.closest('.xz-v2-collection-price, .xz-v2-collection-sort');
      if (!group || group.contains(event.relatedTarget)) return;
      if (group.matches('.xz-v2-collection-sort')) {
        if (this.pendingForm) this.flushPending(event.relatedTarget?.id);
        return;
      }
      // Price: submit once focus leaves both inputs (or on Enter), never mid-typing.
      const url = this.formURL(form);
      if (this.queryKey(url) !== this.queryKey(new URL(window.location.href))) this.navigate(url, { focus: { id: event.relatedTarget?.id } });
    }, { signal });
    for (const type of ['mouseover', 'mouseout']) this.addEventListener(type, (event) => {
      if (window.innerWidth < 992) return;
      const card = event.target.closest('.xz-plp-product-wrp');
      if (!card || !this.contains(card) || card.contains(event.relatedTarget)) return;
      this.animateCard(card, type === 'mouseover', this.motionStart(event));
    }, { signal });
    this.addEventListener('submit', (event) => {
      if (event.target.closest('[data-plp-atc]')) return; // handled by the document-level card add below
      const form = event.target.closest('[data-plp-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      this.navigate(this.formURL(form), { focus: this.focusKey(form) });
    }, { signal });
    this.addEventListener('change', (event) => {
      this.keepPressScroll();
      const staged = event.target.closest('[data-plp-form="mobile"]');
      // Sort does not change any count; a price field reports change on blur or Enter only.
      if (staged && this.contains(staged) && event.target.name !== 'sort_by') this.stageSheet(staged, event.target);
      const form = event.target.closest('[data-plp-form="desktop"]');
      if (!form || !this.contains(form)) return;
      if (event.target.closest('.xz-v2-collection-price')) return;
      // Arrow keys move through the sort radios without reloading; Space, Enter or leaving
      // the group applies the choice (WCAG 3.2.2).
      if (event.target.type === 'radio' && this.arrowKey) {
        this.pendingForm = form;
        return;
      }
      this.pendingForm = null;
      this.navigate(this.formURL(form), { focus: this.focusKey(form, event.target) });
    }, { signal });
    this.sheet?.addEventListener('cancel', (event) => { event.preventDefault(); this.closeSheet(false, this.motionStart(event)); }, { signal });
    // A previous close event can arrive after the same dialog has reopened.
    this.sheet?.addEventListener('close', () => {
      if (!this.sheet?.open) { this.discardStaged(); this.resetSheet(); this.restoreSheet(); }
    }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const location = window.innerWidth <= 991 ? 'mobile' : 'desktop';
      const form = this.querySelector(`[data-plp-form="${location}"]`);
      const block = [...(form?.querySelectorAll('[data-plp-block-id]') || [])].find((node) => node.dataset.plpBlockId === event.detail?.blockId);
      if (!block) return;
      if (location === 'mobile') {
        const opener = this.querySelector('[data-plp-open]');
        if (opener) this.openSheet(opener);
      }
      block.scrollIntoView({ block: 'center', behavior: 'auto' });
      (block.querySelector('input:not([disabled])') || block).focus({ preventScroll: true });
    }, { signal });
    // Chrome fires popstate for same-document #fragment navigation too (the Skip to content
    // link, location.hash, Back from a #hash entry). Only a real change of filters, sort or
    // page, or of the recorded page range, re-renders the listing.
    window.addEventListener('popstate', (event) => {
      const url = new URL(window.location.href);
      if (url.pathname !== new URL(this.dataset.collectionUrl, url).pathname) return;
      const range = event.state?.xzV2Collection;
      const start = range?.sectionId === this.dataset.sectionId && range.url === this.withoutHash(url) ? range.start : null;
      if (this.renderedKey === this.stateKey(url) && (start === null || start === this.pageStart)) return;
      const scroll = window.history.state?.xzV2CollectionScroll;
      this.navigate(url, { history: false, range, restoreY: scroll?.url === url.href ? scroll.y : null });
    }, { signal });
    this.renderedKey = this.stateKey(new URL(window.location.href));
    window.addEventListener('resize', () => {
      if (window.innerWidth > 991 && this.sheet?.open) this.closeSheet(true);
      if (window.innerWidth < 992) this.clearCardMotion();
    }, { signal });
  }

  keepPressScroll() {
    const press = this.pressScroll;
    this.pressScroll = null;
    if (!press) return;
    if (Math.abs(window.scrollY - press.y) >= 1) window.scrollTo({ top: press.y, behavior: 'instant' });
    if (press.scroller?.isConnected && Math.abs(press.scroller.scrollTop - press.top) >= 1) press.scroller.scrollTop = press.top;
  }

  flushPending(focusId) {
    const form = this.pendingForm;
    this.pendingForm = null;
    if (!form || !this.contains(form)) return;
    const url = this.formURL(form);
    if (this.queryKey(url) !== this.queryKey(new URL(window.location.href))) this.navigate(url, { focus: { id: focusId } });
  }

  // Shop by Use preset tag (snippets/xz-v2-collection-use-preset.liquid): the server renders a hidden tag for each use whose
  // filters are exactly the listing's, and the one the URL's xz_use names is shown. Its inline script does that while the
  // page parses; a re-render arrives without scripts, so the listing repeats the choice here, once the URL is the new one
  // (Back/Forward included), in the same task as the swap (no frame without it).
  showUsePreset() {
    const use = new URL(window.location.href).searchParams.get('xz_use');
    for (const tag of this.querySelectorAll('[data-xz-use]')) tag.hidden = tag.dataset.xzUse !== use;
    for (const row of this.querySelectorAll('[data-xz-use-only]')) row.hidden = !row.querySelector('[data-xz-use]:not([hidden])');
  }

  withoutHash(url) {
    const bare = new URL(url);
    bare.hash = '';
    return bare.href;
  }

  // What the listing shows: filters, sort and the last page loaded (never the #fragment).
  stateKey(url) {
    return `${this.queryKey(url)}|${url.searchParams.get('page') || '1'}`;
  }

  // Filter and sort parameters only, in a stable order, with the default sort dropped.
  queryKey(url) {
    return [...url.searchParams].filter(([key, value]) => (key.startsWith('filter.') || key === 'sort_by') && value !== '' && !(key === 'sort_by' && value === this.dataset.defaultSort))
      .map(([key, value]) => `${key}=${value}`).sort().join('&');
  }

  saveScroll() {
    if (this.restoring || !this.isConnected) return;
    try {
      const state = { ...window.history.state, xzV2CollectionScroll: { url: window.location.href, y: Math.round(window.scrollY) } };
      // A #fragment entry starts without state: carry the loaded range onto it.
      const end = Number(this.dataset.currentPage) || 1;
      if (!state.xzV2Collection && this.pageStart && this.pageStart < end) state.xzV2Collection = { sectionId: this.dataset.sectionId, url: this.withoutHash(window.location.href), start: this.pageStart, end };
      window.history.replaceState(state, '', window.location.href);
      if (state.xzV2Collection && state.xzV2Collection.start < state.xzV2Collection.end) this.manualScroll();
    } catch (error) { /* replaceState can be rate-limited; the next save retries. */ }
  }

  // Scroll restoration mode is stored per history entry. The browser restores a plain entry itself,
  // in the first painted frame of a reload or Back (the paint is held until the content is parsed).
  // Only entries the browser cannot restore are switched to manual: one with Load more pages to
  // replay, and entries this listing re-renders in place on popstate (filters and sort).
  manualScroll() {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }

  // A fresh document (Back from a product, reload, bfcache miss) replays the pages loaded with
  // Load more before this one, then returns to the saved scroll position. A plain entry is left to
  // the browser; a manual entry without a range was already scrolled before the first paint by the
  // inline script at the end of <body> (layout/theme.liquid), and is corrected here if the document
  // was too short then.
  restoreOnLoad() {
    const state = window.history.state || {};
    const saved = state.xzV2Collection;
    const scroll = state.xzV2CollectionScroll;
    const y = scroll?.url === window.location.href && Number.isFinite(scroll.y) ? scroll.y : null;
    const end = Number(this.dataset.currentPage) || 1;
    const valid = saved?.sectionId === this.dataset.sectionId && saved.url === this.withoutHash(window.location.href) &&
      Number.isInteger(saved.start) && saved.start >= 1 && saved.start < end && saved.end === end;
    if (!valid) {
      if (y !== null && window.history.scrollRestoration === 'manual' && Math.round(window.scrollY) !== y) window.scrollTo(0, y);
      return;
    }
    this.manualScroll();
    this.restoring = true;
    const controller = this.pending = new AbortController();
    (async () => {
      const cards = [];
      for (let page = saved.start; page < end; page++) {
        const pageURL = new URL(window.location.href);
        pageURL.searchParams.set('page', String(page));
        const prior = await this.fetchSection(pageURL, controller.signal);
        if (Number(prior.dataset.currentPage) !== page) throw new Error('Collection page changed');
        cards.push(...prior.querySelector('[data-plp-grid]').children);
      }
      if (controller.signal.aborted || !this.isConnected) return;
      this.querySelector('[data-plp-grid]')?.prepend(...cards);
      this.pageStart = saved.start;
      this.updatePrev();
    })().catch(() => {
      // The page stays on its native single page; record that so Back does not retry.
      window.history.replaceState({ ...window.history.state, xzV2Collection: { sectionId: this.dataset.sectionId, url: this.withoutHash(window.location.href), start: end, end } }, '', window.location.href);
    }).finally(() => {
      if (this.pending === controller) this.pending = null;
      this.restoring = false;
      if (y !== null && this.isConnected) window.scrollTo(0, y);
    });
  }

  // "Show previous products" loads the page before the first one shown.
  updatePrev() {
    const region = this.querySelector('[data-plp-pagination="prev"]');
    if (!region) return;
    const link = region.querySelector('[data-plp-load-prev]');
    if (this.pageStart <= 1) { region.replaceChildren(); return; }
    if (link) {
      const url = new URL(link.href, window.location.href);
      if (this.pageStart - 1 <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(this.pageStart - 1));
      link.href = url.pathname + url.search;
    }
  }

  async loadPrevious(link) {
    if (this.pending || this.pageStart <= 1) return;
    const controller = this.pending = new AbortController();
    const grid = this.querySelector('[data-plp-grid]');
    const anchor = grid?.firstElementChild;
    const before = anchor?.getBoundingClientRect().top;
    const status = this.querySelector('[data-plp-status]');
    try {
      const pageURL = new URL(window.location.href);
      pageURL.searchParams.set('page', String(this.pageStart - 1));
      const prior = await this.fetchSection(pageURL, controller.signal);
      if (controller.signal.aborted || !this.isConnected || !grid) return;
      const cards = [...prior.querySelector('[data-plp-grid]').children];
      grid.prepend(...cards);
      this.pageStart = Number(prior.dataset.currentPage) || this.pageStart - 1;
      this.updatePrev();
      if (anchor && before !== undefined) window.scrollBy(0, anchor.getBoundingClientRect().top - before);
      const saved = window.history.state?.xzV2Collection;
      const end = Number(this.dataset.currentPage) || 1;
      const here = this.withoutHash(window.location.href);
      window.history.replaceState({ ...window.history.state, xzV2Collection: { sectionId: this.dataset.sectionId, url: here, start: this.pageStart, end: saved?.url === here ? saved.end : end } }, '', window.location.href);
      this.manualScroll();
      cards[0]?.querySelector('.xz-v2-collection-card-link')?.focus({ preventScroll: true });
      if (status) status.textContent = this.dataset.loadedLabel || this.resultsLabel(this);
    } catch (error) {
      if (!controller.signal.aborted) window.location.assign(link.href);
    } finally {
      if (this.pending === controller) this.pending = null;
    }
  }

  async fetchSection(pageURL, signal) {
    const request = new URL(pageURL);
    request.searchParams.set('section_id', this.dataset.sectionId);
    const response = await fetch(request, { signal });
    if (!response.ok) throw new Error('Collection unavailable');
    const html = await response.text();
    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('xz-v2-collection');
    if (!incoming || incoming.dataset.sectionId !== this.dataset.sectionId || !incoming.querySelector('[data-plp-results]') || !incoming.querySelector('[data-plp-grid]')) throw new Error('Collection section missing');
    // DOMParser disables scripting: noscript STYLE children become active when adopted into
    // the live page, and the inline legacy-detection script must not travel either.
    incoming.querySelectorAll('noscript, script').forEach((node) => node.remove());
    return incoming;
  }

  disconnectedCallback() {
    this.events?.abort();
    clearTimeout(this.scrollTimer);
    this.pending?.abort();
    this.cancelStage();
    this.loadMoreObserver?.disconnect();
    clearTimeout(this.clockTimer);
    this.releaseClock?.();
    this.releaseClock = null;
    this.prefetched?.controller.abort();
    this.prefetched = null;
    this.clearCardMotion();
    // Editor moves can reconnect this exact node; native open state must not survive.
    if (this.sheet?.open) this.sheet.close();
    this.resetSheet();
    this.restoreSheet(false);
  }

  // Card hover, Milen's IX2 a-38 (in) / a-39 (out), desktop (>=992) only: card background
  // #000 -> #181818 and image scale 1 -> 1.03, 500ms outQuad; a reversal runs the full 500ms
  // from wherever the card is, as IX2 does (a CSS transition would shorten it). Web Animations
  // keep the zoom on the compositor and start at the hover event, so the first frame already
  // moves forward (the old rAF loop started late and stepped backwards first).
  animateCard(card, entering, startTime = performance.now()) {
    const prior = this.cardMotion.get(card);
    const from = prior ? this.cardValue(prior) : 0;
    const to = entering ? 1 : 0;
    prior?.animations.forEach((animation) => animation.cancel());
    this.cardMotion.delete(card);
    if (!entering && from === 0) return;
    const image = card.querySelector('.ff-image-product-box');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timing = { duration: reduced ? 0 : 500, easing: OUT_QUAD, fill: 'both' };
    const shade = (value) => { const channel = Math.round(24 * value); return `rgb(${channel}, ${channel}, ${channel})`; };
    const zoom = (value) => `scale3d(${1 + 0.03 * value}, ${1 + 0.03 * value}, 1)`;
    if (typeof card.animate !== 'function') {
      card.style.backgroundColor = to ? shade(1) : '';
      if (image) image.style.transform = to ? zoom(1) : '';
      return;
    }
    const animations = [card.animate({ backgroundColor: [shade(from), shade(to)] }, timing)];
    if (image) animations.push(image.animate({ transform: [zoom(from), zoom(to)] }, timing));
    const start = Math.min(startTime, document.timeline?.currentTime ?? startTime);
    if (!reduced) for (const animation of animations) { try { animation.startTime = start; } catch (error) { /* keeps its own start */ } }
    const state = { from, to, animations };
    this.cardMotion.set(card, state);
    // Hover-out leaves nothing behind: at #000 / scale 1 the card equals its resting style.
    if (!entering) animations[0].finished?.then(() => {
      if (this.cardMotion.get(card) !== state) return;
      animations.forEach((animation) => animation.cancel());
      this.cardMotion.delete(card);
    }).catch(() => {});
  }

  holdClock(on) {
    clearTimeout(this.clockTimer);
    if (on) {
      if (!this.releaseClock) this.releaseClock = window.XZV2FrameClock?.acquire?.() || null;
      return;
    }
    // A tap leaves before its click fires: keep ticking long enough for the click to use it.
    this.clockTimer = setTimeout(() => { this.releaseClock?.(); this.releaseClock = null; }, 1000);
  }

  motionStart(event) {
    const now = performance.now();
    if (this.releaseClock && window.XZV2FrameClock) return Math.min(window.XZV2FrameClock.now(), now);
    return eventStart(event);
  }

  // Where a card's hover tween is now (0 rest, 1 hovered), read from the running animation.
  cardValue(state) {
    const animation = state.animations[0];
    let progress = animation.effect?.getComputedTiming?.().progress;
    if (typeof progress !== 'number') {
      const t = Math.min(1, Math.max(0, (Number(animation.currentTime) || 0) / 500));
      progress = animation.playState === 'finished' ? 1 : t * (2 - t);
    }
    return state.from + (state.to - state.from) * progress;
  }

  clearCardMotion() {
    for (const [card, state] of this.cardMotion || []) {
      state.animations.forEach((animation) => animation.cancel());
      card.style.backgroundColor = '';
      card.querySelector('.ff-image-product-box')?.style.removeProperty('transform');
    }
    this.cardMotion?.clear();
  }

  // Min above Max (300 / 100) means the range between them: the two fields swap, visibly, before
  // the URL is built (sidebar blur or Enter, the sheet's APPLY, and its staged counts).
  orderPrice(form) {
    const min = form.querySelector('.xz-v2-collection-price input[name$=".gte"]');
    const max = form.querySelector('.xz-v2-collection-price input[name$=".lte"]');
    if (!min || !max || min.value === '' || max.value === '') return;
    const low = Number(min.value), high = Number(max.value);
    if (!Number.isFinite(low) || !Number.isFinite(high) || low <= high) return;
    const value = min.value;
    min.value = max.value;
    max.value = value;
  }

  formURL(form) {
    this.orderPrice(form);
    const current = new URL(window.location.href);
    const url = new URL(this.dataset.collectionUrl, current);
    url.search = current.search;
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || ['sort_by', 'page', 'section_id', 'sections'].includes(key)) url.searchParams.delete(key);
    }
    for (const [key, value] of new FormData(form)) {
      if (typeof value === 'string' && value !== '' && (key.startsWith('filter.') || key === 'sort_by')) url.searchParams.append(key, value);
    }
    if (url.searchParams.get('sort_by') === this.dataset.defaultSort) url.searchParams.delete('sort_by');
    return url;
  }

  focusKey(form, field = document.activeElement) {
    return form.dataset.plpForm === 'mobile' ? { open: true } : { id: field?.id };
  }

  onClick(event) {
    if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const control = event.target.closest('[data-plp-open], [data-plp-close], [data-plp-clear], [data-plp-remove], [data-plp-load-more], [data-plp-load-prev]');
    if (!control || !this.contains(control)) return;
    if (control.hasAttribute('data-plp-open')) { event.preventDefault(); this.openSheet(control, this.motionStart(event)); return; }
    if (control.hasAttribute('data-plp-close')) { event.preventDefault(); this.closeSheet(false, this.motionStart(event)); return; }
    const url = this.controlURL(control);
    if (!url) return;
    event.preventDefault();
    if (control.hasAttribute('data-plp-load-prev')) { this.loadPrevious(control); return; }
    this.navigate(url, { append: control.hasAttribute('data-plp-load-more'), focus: this.sheet?.open ? { open: true } : { results: true } });
  }

  // The same-collection URL a listing link loads in place, or null for a plain navigation.
  controlURL(control) {
    if (!control.href) return null;
    const url = new URL(control.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== new URL(this.dataset.collectionUrl, url).pathname) return null;
    for (const key of ['view', 'preview_theme_id']) {
      const value = new URL(window.location.href).searchParams.get(key);
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    }
    return url;
  }

  // Milen's LOAD MORE is a static button: the next cards simply appear. The next page's HTML is
  // fetched once the button nears the viewport, and its photos are warmed when the shopper points
  // at or focuses it, so a click appends finished cards at once (inside the 500ms input window, so
  // the grid growing is not a layout shift) instead of dimming the grid and popping photos in.
  watchLoadMore() {
    this.loadMoreObserver?.disconnect();
    if (!('IntersectionObserver' in window)) return;
    this.loadMoreObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) if (entry.isIntersecting) this.prefetchMore(entry.target);
    }, { rootMargin: '0px 0px 600px 0px' });
    this.querySelectorAll('[data-plp-load-more]').forEach((link) => this.loadMoreObserver.observe(link));
  }

  prefetchMore(control, warm = false) {
    if (!control?.isConnected || control.getClientRects().length === 0) return;
    const url = this.controlURL(control);
    if (!url) return;
    let entry = this.prefetched;
    if (entry?.href !== url.href) {
      entry?.controller.abort();
      const controller = new AbortController();
      entry = this.prefetched = { href: url.href, controller, promise: this.fetchSection(url, controller.signal) };
      entry.promise.catch(() => { if (this.prefetched === entry) this.prefetched = null; });
    }
    if (warm && !entry.warm) {
      entry.warm = entry.promise.then((incoming) => {
        const first = [...incoming.querySelector('[data-plp-grid]').children].slice(0, 6);
        entry.warmed = first.flatMap((card) => [...card.querySelectorAll('img')]).map((img) => {
          const copy = new Image();
          if (img.sizes) copy.sizes = img.sizes;
          if (img.srcset) copy.srcset = img.srcset;
          copy.src = img.src;
          return copy;
        });
      }).catch(() => {});
    }
  }

  // New cards join the page with their photos decoded (bounded wait), never as empty boxes. The
  // first two rows are the ones in view where the button was; later cards keep lazy loading.
  async readyCards(cards) {
    const images = [];
    cards.forEach((card, index) => {
      document.adoptNode(card);
      if (index < 6) card.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; images.push(img); });
    });
    if (!images.length || typeof images[0].decode !== 'function') return;
    await Promise.race([
      Promise.all(images.map((img) => img.decode().catch(() => {}))),
      new Promise((resolve) => { setTimeout(resolve, 400); }),
    ]);
  }

  openSheet(opener, startTime) {
    if (!this.sheet || (this.sheet.open && !this.sheetClosing)) return;
    this.opener = opener;
    // Any attribute change here restyles the whole page (the base theme's broad :has() rules), and
    // showModal() forces that style pass at once for focus. Setting aria-expanded first folds both
    // changes into that one pass instead of paying for a second one in the first frame.
    opener.setAttribute('aria-expanded', 'true');
    if (!this.sheet.open) this.sheet.showModal();
    lockScroll(this.sheet);
    this.lockedSheet = this.sheet;
    this.animateSheet(true, false, startTime);
  }

  closeSheet(immediate = false, startTime) {
    if (!this.sheet?.open) return;
    this.animateSheet(false, immediate, startTime);
  }

  // Milen's IX2 a-40 (open: parts -24px -> 0 over 500ms and sheet opacity 0 -> 1 over 200ms,
  // outQuad) and a-41 (close: parts -> -88px and opacity -> 0 over 100ms, inQuad, then hidden and
  // parts back at -24px). IX2 starts an action at its last frame tick before the tap, so its first
  // painted frame already shows one frame of progress; document.timeline.currentTime is that tick.
  // Web Animations run on the compositor: a busy main thread (a mid-range phone laying out the
  // sheet, or a session recorder reacting to style writes) cannot freeze or skip the fade, and no
  // per-frame inline-style mutations are made. A reversal starts from the exact current values.
  animateSheet(opening, immediate = false, startTime = performance.now()) {
    const sheet = this.sheet;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const now = performance.now();
    const tick = document.timeline?.currentTime;
    const start = Math.min(startTime, now, Number.isFinite(tick) && now - tick < 100 ? tick : Infinity);
    const current = this.sheetValues(start);
    this.stopSheetMotion();
    const generation = this.motionGeneration = (this.motionGeneration || 0) + 1;
    this.sheetClosing = !opening;
    const motion = {
      opening, start, fromX: current.x, fromOpacity: current.opacity,
      toX: opening ? 0 : -88, toOpacity: opening ? 1 : 0,
      duration: opening ? 500 : 100, fade: opening ? 200 : 100,
      easing: opening ? OUT_QUAD : IN_QUAD, animations: [],
    };
    const finish = () => {
      if (generation !== this.motionGeneration) return;
      this.stopSheetMotion(motion.toX, motion.toOpacity);
      if (!opening) {
        // Unapplied choices are discarded: the sheet reopens on the current results.
        sheet.querySelector('[data-plp-form="mobile"]')?.reset();
        this.discardStaged();
        this.opener?.setAttribute('aria-expanded', 'false'); // one style pass with close(), as on open
        sheet.close();
        this.resetSheet();
        this.restoreSheet();
      }
    };
    if (immediate || reduced) { finish(); return; }
    this.sheetMotion = motion;
    if (typeof sheet.animate !== 'function') { this.sheetFallback(motion, generation, finish); return; }
    const move = (x) => ({ transform: `translate3d(${x}px,0,0)` });
    const timing = (duration) => ({ duration, easing: motion.easing, fill: 'both' });
    motion.animations = [
      sheet.animate([{ opacity: motion.fromOpacity }, { opacity: motion.toOpacity }], timing(motion.fade)),
      ...this.sheetParts.map((part) => part.animate([move(motion.fromX), move(motion.toX)], timing(motion.duration))),
    ];
    const retime = (at) => {
      motion.start = at;
      for (const animation of motion.animations) { try { animation.startTime = at; } catch (error) { /* keeps its own start */ } }
    };
    // On time (desktops, recent phones: the tap handler is done within a few frames) the tweens are
    // anchored at the tap's frame tick, exactly as IX2 runs, so the first painted frame shows one
    // frame of progress. When the handler ran long (a slower phone restyling the page for the modal
    // sheet), the first frame is late: the tweens then stay pending at their start values and begin
    // when the sheet is first painted, so the whole fade is seen instead of a jump to nearly opaque.
    if (performance.now() - start <= LATE) retime(start);
    else {
      motion.start = null;
      const first = motion.animations[0];
      Promise.resolve(first.ready).then(() => {
        if (generation === this.motionGeneration && motion.start === null && first.startTime !== null) retime(first.startTime);
      }, () => {});
    }
    const last = motion.animations[motion.animations.length - 1];
    if (last.finished) last.finished.then(finish, () => {});
    else last.onfinish = finish;
  }

  // Where the sheet is at `now` (IX2's own formulas), so a reversal continues from what is painted.
  sheetValues(now, motion = this.sheetMotion) {
    if (!motion) return { x: this.sheetX ?? -24, opacity: this.sheetOpacity ?? 0 };
    if (motion.start === null) return { x: motion.fromX, opacity: motion.fromOpacity }; // not started yet
    const ease = (p) => (motion.opening ? p * (2 - p) : p * p);
    const at = (duration) => ease(Math.min(1, Math.max(0, (now - motion.start) / duration)));
    return {
      x: motion.fromX + (motion.toX - motion.fromX) * at(motion.duration),
      opacity: motion.fromOpacity + (motion.toOpacity - motion.fromOpacity) * at(motion.fade),
    };
  }

  // Ends the running tween; with values, they are written once as the resting inline style.
  stopSheetMotion(x, opacity) {
    cancelAnimationFrame(this.motionFrame);
    this.motionFrame = null;
    const motion = this.sheetMotion;
    this.sheetMotion = null;
    if (x !== undefined) {
      this.sheetX = x;
      this.sheetOpacity = opacity;
      for (const part of this.sheetParts || []) part.style.transform = `translate3d(${x}px,0,0)`;
      if (this.sheet) this.sheet.style.opacity = String(opacity);
    } else if (motion) {
      const values = this.sheetValues(document.timeline?.currentTime ?? performance.now(), motion);
      this.sheetX = values.x;
      this.sheetOpacity = values.opacity;
    }
    motion?.animations.forEach((animation) => animation.cancel());
  }

  // Engines without element.animate: the same tween as a rAF loop.
  sheetFallback(motion, generation, finish) {
    let first = true;
    const frame = (now) => {
      if (generation !== this.motionGeneration) return;
      // A late first frame starts the tween one frame back, so the fade is still seen.
      if (first) { first = false; if (now - motion.start > LATE) motion.start = now - FRAME; }
      const values = this.sheetValues(now);
      for (const part of this.sheetParts) part.style.transform = `translate3d(${values.x}px,0,0)`;
      this.sheet.style.opacity = String(values.opacity);
      if (now - motion.start < motion.duration) this.motionFrame = requestAnimationFrame(frame);
      else finish();
    };
    this.motionFrame = requestAnimationFrame(frame);
  }

  resetSheet() {
    this.motionGeneration = (this.motionGeneration || 0) + 1;
    this.sheetClosing = false;
    this.stopSheetMotion(-24, 0);
    if (this.sheet) this.sheet.style.transform = '';
  }

  restoreSheet(restoreFocus = true) {
    if (this.lockedSheet) {
      unlockScroll(this.lockedSheet);
      this.lockedSheet = null;
    }
    if (this.opener?.getAttribute('aria-expanded') !== 'false') this.opener?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    this.opener = null;
  }

  // Staged choices in the open sheet refresh its counts, zero-count disabled rows and inert groups
  // from the section rendered for the staged URL, as the sidebar does after every click (an inert
  // group on screen stays in place, disabled, until APPLY), without applying anything: no history
  // entry, the grid stays, the sheet stays open with focus and scroll.
  // Requests are debounced (300ms) and a newer change aborts an older request.
  stageSheet(form, changed) {
    this.cancelStage();
    if (!this.sheet?.open || this.sheetClosing || !this.sheet.contains(form)) return;
    if (changed) this.stageAnchor = changed;
    this.stageTimer = setTimeout(async () => {
      if (!this.sheet?.open || this.sheetClosing || !this.isConnected) return;
      // Never build a URL from a price that is still being typed (focusout runs this again).
      const typing = document.activeElement?.closest?.('.xz-v2-collection-price');
      if (typing && form.contains(typing)) { this.stageDeferred = true; return; }
      this.stageDeferred = false;
      const controller = this.staging = new AbortController();
      try {
        const incoming = await this.fetchSection(this.formURL(form), controller.signal);
        if (this.staging !== controller || !this.sheet?.open || this.sheetClosing || !form.isConnected) return;
        this.patchSheet(form, incoming.querySelector('[data-plp-form="mobile"]'));
      } catch (error) {
        /* The counts stay as they were; APPLY FILTERS still loads the real result. */
      } finally {
        if (this.staging === controller) this.staging = null;
      }
    }, 300);
  }

  cancelStage() {
    clearTimeout(this.stageTimer);
    this.stageTimer = null;
    this.staging?.abort();
    this.staging = null;
  }

  // Closing without APPLY puts the sheet back as the server rendered it (form.reset() restores only
  // the ticks and fields).
  discardStaged() {
    this.cancelStage();
    this.stageDeferred = false;
    this.stageAnchor = null;
    const saved = this.sheetSaved;
    this.sheetSaved = null;
    if (!saved) return;
    saved.inserted.forEach((node) => node.remove());
    for (const [input, disabled, count, text] of saved.rows) {
      if (input) input.disabled = disabled;
      if (count) count.textContent = text;
    }
    for (const group of saved.groups) this.showGroup(group, true);
  }

  showGroup(group, show) {
    const rule = group.previousElementSibling?.classList.contains('xz-line-2') ? group.previousElementSibling : null;
    for (const node of [rule, group]) if (node && node.hidden === show) node.hidden = !show;
  }

  patchSheet(form, next) {
    if (!next) return;
    const scroller = form.closest('.xz-filter-wrp-mobile');
    // The control in use stays where it is on screen while groups above it join or return.
    const focused = form.contains(document.activeElement) ? document.activeElement : null;
    const anchorControl = focused || (this.stageAnchor?.isConnected && form.contains(this.stageAnchor) ? this.stageAnchor : null);
    const anchor = anchorControl?.closest('label') || anchorControl;
    const before = anchor?.getClientRects().length ? anchor.getBoundingClientRect().top : null;
    if (!this.sheetSaved) {
      this.sheetSaved = {
        rows: [...form.querySelectorAll('label.xz-filter-parent')].map((label) => {
          const input = label.querySelector('input');
          const count = label.querySelector('.ff-caption-1');
          return [input, input?.disabled, count, count?.textContent];
        }),
        groups: [...form.querySelectorAll('fieldset[data-plp-facet]')].filter((group) => !group.hidden),
        inserted: [],
      };
    }
    const groups = (root) => new Map([...root.querySelectorAll('fieldset[data-plp-facet]')].map((group) => [group.dataset.plpFacet, group]));
    const current = groups(form);
    const fresh = groups(next);
    let after = form.querySelector('.xz-v2-collection-sort');
    for (const [key, group] of fresh) {
      let mine = current.get(key);
      if (!mine) {
        // A group the applied listing could not narrow, which the staged one can: it joins in place.
        if (!after) break;
        const rule = group.previousElementSibling?.classList.contains('xz-line-2') ? group.previousElementSibling : null;
        const nodes = [rule, group].filter(Boolean).map((node) => document.adoptNode(node));
        after.after(...nodes);
        this.sheetSaved.inserted.push(...nodes);
        mine = group;
      } else {
        this.showGroup(mine, true);
        const twins = new Map([...group.querySelectorAll('input[type="checkbox"]')].map((input) => [input.id, input]));
        for (const input of mine.querySelectorAll('input[type="checkbox"]')) {
          const twin = twins.get(input.id);
          const count = input.closest('label')?.querySelector('.ff-caption-1');
          const text = twin?.closest('label')?.querySelector('.ff-caption-1')?.textContent ?? '(0)';
          if (count && count.textContent !== text) count.textContent = text;
          // A ticked row is never disabled, nor the focused one (focus would fall out of the dialog).
          const disabled = !input.checked && input !== document.activeElement && (twin ? twin.disabled : true);
          if (input.disabled !== disabled) input.disabled = disabled;
        }
      }
      after = mine;
    }
    // A group the staged listing cannot narrow leaves, as it does from the sidebar after a click, only
    // where nothing on screen moves: wholly below the visible list and after the control in use. Any
    // other one stays in place until APPLY (the server render then decides) or close, unless it holds a
    // tick or the focus: its open rows are disabled and their counts, which the staged render does not
    // give, are left blank rather than stale. Removing it would move rows under the finger: above the
    // control in use, a sheet at scrollTop 0 cannot absorb rows moving up (filtering-r3-1); on screen
    // below it, the rows under it jump up, and a list scrolled to its end gets shorter than its scroll
    // position, so the browser clamps scrollTop and every row moves down (filtering-r4-1).
    const visibleBottom = scroller?.getClientRects().length ? scroller.getBoundingClientRect().bottom : null;
    for (const [key, mine] of current) {
      if (fresh.has(key) || mine.hidden || mine.querySelector('input:checked') || mine.contains(document.activeElement)) continue;
      const lead = mine.previousElementSibling?.classList.contains('xz-line-2') ? mine.previousElementSibling : mine;
      const afterAnchor = !anchor || (anchor.compareDocumentPosition(mine) & Node.DOCUMENT_POSITION_FOLLOWING);
      if (afterAnchor && visibleBottom !== null && lead.getBoundingClientRect().top >= visibleBottom) {
        this.showGroup(mine, false);
        continue;
      }
      for (const label of mine.querySelectorAll('label.xz-filter-parent')) {
        const input = label.querySelector('input[type="checkbox"]');
        if (input && !input.disabled) input.disabled = true;
        const count = label.querySelector('.ff-caption-1');
        if (count?.textContent) count.textContent = '';
      }
    }
    if (scroller && before !== null && anchor.getClientRects().length) scroller.scrollTop += anchor.getBoundingClientRect().top - before;
  }

  paginationPairs(incoming) {
    return ['desktop', 'mobile'].map((location) => {
      const selector = `[data-plp-pagination="${location}"]`;
      const current = this.querySelector(selector), next = incoming.querySelector(selector);
      if (!current || !next) throw new Error('Collection pagination missing');
      return [current, next];
    });
  }

  // The live status after a re-render (unless the section sets data-loaded-label): the product count
  // from [data-plp-count] (the listing's total) and how many cards are on the page, e.g.
  // "Showing 21 products" or "Showing 48 of 265 products".
  resultsLabel(owner = this) {
    const shown = owner.querySelectorAll('[data-plp-grid] > li.xz-plp-product-wrp').length;
    const total = parseInt(owner.querySelector('[data-plp-count]')?.textContent, 10);
    if (!shown) return 'No products match these filters';
    if (!Number.isFinite(total) || shown >= total) return `Showing ${shown} product${shown === 1 ? '' : 's'}`;
    return `Showing ${shown} of ${total} products`;
  }

  // The desktop sidebar is not sticky (Milen's design): a box ticked far down it re-renders the listing
  // while the page stays scrolled, and a short result can sit entirely above the viewport, leaving an
  // empty-looking frame. Then the results (active-filter chips first) are brought up to just under the
  // sticky header. Results already in view, or still below the fold, are left where they are.
  revealResults() {
    const results = this.querySelector('[data-plp-results]');
    if (!results) return;
    // The header's full height when shown (promo bar + nav), which scrolling up brings back into view.
    const placeholder = document.querySelector('.xz-nav-placeholder');
    const header = placeholder ? Math.max(0, Math.round(placeholder.offsetHeight)) : parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    const cards = [...this.querySelectorAll('[data-plp-grid] > li.xz-plp-product-wrp')];
    const items = cards.length ? cards : [this.querySelector('.xz-v2-collection-empty')].filter(Boolean);
    if (!items.length) return;
    const view = window.innerHeight;
    if (items.some((item) => { const box = item.getBoundingClientRect(); return box.bottom > header && box.top < view; })) return false;
    if (items[0].getBoundingClientRect().top >= view) return false;
    const top = Math.max(0, Math.round(results.getBoundingClientRect().top + window.scrollY - header));
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
    return true;
  }

  // Shows a focused control the page left outside the viewport (or under the header shown on
  // scrolling up); one already in view stays where it is. Hidden checkboxes show through their label.
  revealFocus(target) {
    const box = target.closest('label') || target;
    if (!box.getClientRects().length) return;
    const placeholder = document.querySelector('.xz-nav-placeholder');
    const header = placeholder ? Math.max(0, Math.round(placeholder.offsetHeight)) : 0;
    const rect = box.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;
    if (rect.bottom > window.innerHeight) { box.scrollIntoView({ block: 'nearest' }); return; }
    window.scrollBy(0, Math.round(rect.top - header - 16));
  }

  async navigate(url, { append = false, history = true, focus = {}, range = null, restoreY = null } = {}) {
    this.pending?.abort();
    const pending = this.pending = new AbortController();
    const scrollY = window.scrollY;
    const results = this.querySelector('[data-plp-results]');
    const status = this.querySelector('[data-plp-status]');
    results?.setAttribute('aria-busy', 'true');
    // Milen's grid has no busy state: only a slow response dims it (never a flash on a fast one).
    clearTimeout(this.slowTimer);
    this.slowTimer = setTimeout(() => { if (this.pending === pending) results?.setAttribute('data-plp-slow', ''); }, 800);
    if (status) status.textContent = this.dataset.loadingLabel || 'Loading products…';
    this.closeSheet(true);
    const currentRequest = () => pending === this.pending && !pending.signal.aborted && this.isConnected;
    const entry = this.prefetched;
    this.prefetched = null;
    const prefetched = append && entry?.href === url.href ? entry : null;
    if (entry && !prefetched) entry.controller.abort();
    const fetchPage = async (pageURL) => {
      let incoming = null;
      if (prefetched && pageURL === url) incoming = await prefetched.promise.catch(() => null);
      if (!incoming) incoming = await this.fetchSection(pageURL, pending.signal);
      if (!currentRequest()) throw new Error('Superseded collection request');
      this.paginationPairs(incoming);
      return incoming;
    };
    try {
      const incoming = await fetchPage(url);
      const end = Number(incoming.dataset.currentPage);
      let start = append ? this.pageStart : end;
      // O(1) history metadata, replay bounded by the server's actual page count.
      // A plain/reloaded URL continues to mean its native single Shopify page.
      if (!append && range?.sectionId === this.dataset.sectionId && range.url === this.withoutHash(url) &&
          Number.isInteger(range.start) && Number.isInteger(range.end) && range.start >= 1 &&
          range.start < range.end && range.end === end && range.end <= Number(incoming.dataset.pageCount)) {
        const cards = [];
        for (let page = range.start; page < end; page++) {
          const pageURL = new URL(url);
          pageURL.searchParams.set('page', String(page));
          const prior = await fetchPage(pageURL);
          if (Number(prior.dataset.currentPage) !== page) throw new Error('Collection page changed');
          cards.push(...prior.querySelector('[data-plp-grid]').children);
        }
        incoming.querySelector('[data-plp-grid]').prepend(...cards);
        start = range.start;
      }
      if (!currentRequest()) return;
      let owner = this;
      let firstNew = null;
      // The sidebar control in use (the box just ticked) stays where it is on screen while groups above
      // it hide or return in the new render, so a second click lands on the row the shopper aimed at
      // (filtering-r3-1), as patchSheet() keeps it in the sheet.
      const anchorTop = (id) => {
        const control = id && !append && history && restoreY === null ? document.getElementById(id) : null;
        const box = control?.closest('label') || control;
        return box?.getClientRects().length ? box.getBoundingClientRect().top : null;
      };
      const anchorBefore = anchorTop(focus.id);
      if (append) {
        const grid = this.querySelector('[data-plp-grid]');
        const newGrid = incoming.querySelector('[data-plp-grid]');
        const pagination = this.paginationPairs(incoming);
        if (!grid || !newGrid) throw new Error('Collection page missing');
        const cards = [...newGrid.children];
        await this.readyCards(cards);
        if (!currentRequest()) return;
        firstNew = cards[0] || null;
        grid.append(...cards);
        for (const [current, next] of pagination) current.replaceWith(next);
        this.watchLoadMore();
        this.dataset.currentPage = incoming.dataset.currentPage;
        this.dataset.pageCount = incoming.dataset.pageCount;
        results?.removeAttribute('aria-busy');
        results?.removeAttribute('data-plp-slow');
      } else {
        incoming.pageStart = start;
        this.pending = null;
        this.swapping = true;
        try { this.replaceWith(incoming); } finally { this.swapping = false; }
        owner = incoming;
      }
      // The entry left behind and the new one are re-rendered in place on popstate: manual restoration.
      if (history && (!append || start < end)) this.manualScroll();
      if (history) window.history[append ? 'replaceState' : 'pushState']({ ...window.history.state, xzV2Collection: { sectionId: this.dataset.sectionId, url: this.withoutHash(url), start, end } }, '', url);
      owner.renderedKey = owner.stateKey(new URL(window.location.href));
      if (!append) owner.showUsePreset();
      window.scrollTo({ top: restoreY ?? scrollY, behavior: 'instant' });
      if (anchorBefore !== null) {
        const anchorAfter = owner.contains(document.getElementById(focus.id)) ? anchorTop(focus.id) : null;
        if (anchorAfter !== null && Math.abs(anchorAfter - anchorBefore) >= 1) window.scrollBy({ top: anchorAfter - anchorBefore, behavior: 'instant' });
      }
      // Load more: this entry's saved scroll belongs to the page=N URL it now has, so a reload straight
      // after the click replays the pages and returns here (filtering-r3-9).
      if (append && history) this.saveScroll();
      // Load more: focus moves to the first new product, not back to product 1.
      let target = firstNew ? firstNew.querySelector('.xz-v2-collection-card-link') : focus.open ? owner.querySelector('[data-plp-open]') : focus.id ? document.getElementById(focus.id) : null;
      if (!target || !owner.contains(target)) target = owner.querySelector('[data-plp-results]');
      target?.focus({ preventScroll: true });
      // A new filter or sort (not Back/Forward, not Load more) whose results all sit above the viewport.
      const revealed = !append && history && owner.revealResults?.();
      // Otherwise a sidebar control focused after the re-render (Tab out of Sort applies it) is brought
      // into view; results brought up to the header take the sidebar's top with them.
      if (!revealed && target && focus.id && target.id === focus.id) this.revealFocus(target);
      const liveStatus = owner.querySelector('[data-plp-status]');
      if (liveStatus) liveStatus.textContent = owner.dataset.loadedLabel || this.resultsLabel(owner);
    } catch (error) {
      if (pending !== this.pending || pending.signal.aborted || !this.isConnected) return;
      window.location.assign(url.href);
    } finally {
      if (this.pending === pending) {
        this.pending = null;
        clearTimeout(this.slowTimer);
        results?.removeAttribute('aria-busy');
        results?.removeAttribute('data-plp-slow');
      }
    }
  }
}

if (!customElements.get('xz-v2-collection')) customElements.define('xz-v2-collection', XinzuoV2Collection);

/* ---- Card cart button (PLP, search and any page that renders xz-v2-collection-card) ----
   The add is taken on the button's click with preventDefault, so the /cart/add form is never
   submitted: a submitted form makes app web pixel 1735589939 throw on product_added_to_cart.
   Enter/Space on the button also fire click. A form submit that still arrives (no click, e.g.
   an implicit submission) is routed to the same add. Any failure falls back to the product page.
   After the add, focus moves into the cart drawer and returns to the card button on close. */
const FEE_VARIANT_IDS = [43781283217459, 43781283250227];
function focusCartDrawer(returnTarget) {
  let tries = 0;
  let watched = null;
  const attempt = () => {
    const dialog = document.querySelector('cart-drawer-component dialog[open]');
    if (dialog && watched !== dialog) {
      watched = dialog;
      dialog.addEventListener('close', () => {
        tries = 25;
        setTimeout(() => { if (returnTarget?.isConnected) returnTarget.focus(); }, 0);
      }, { once: true });
    }
    if (tries >= 25) return;
    if (dialog && !dialog.contains(document.activeElement)) {
      const target = dialog.querySelector('[autofocus], button[aria-label], a[href], button:not([disabled])');
      if (target) { target.focus(); return; }
    }
    if (++tries < 25) setTimeout(attempt, 100);
  };
  setTimeout(attempt, 50);
}
async function addCardToCart(form) {
  if (form.dataset.busy === 'true') return;
  form.dataset.busy = 'true';
  const button = form.querySelector('button');
  button?.setAttribute('aria-busy', 'true');
  const status = form.closest('xz-v2-collection')?.querySelector('[data-plp-status]');
  const variantId = String(new FormData(form).get('id'));
  const root = window.Shopify?.routes?.root || '/';
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))];
  try {
    // Same request shape as the product form (product-form.js), which app web pixels expect.
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: {}, ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {}) }),
    });
    if (!response.ok) throw new Error('add failed');
    const data = await response.json().catch(() => ({}));
    const sections = data.sections || {};
    let itemCount;
    for (const html of Object.values(sections)) {
      const count = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
      if (count) { itemCount = parseInt(count.textContent, 10) || 0; break; }
    }
    if (itemCount === undefined) {
      const cart = await fetch(`${root}cart.js`).then((r) => r.json()).catch(() => null);
      itemCount = cart ? (cart.items || []).reduce((n, item) => (FEE_VARIANT_IDS.includes(item.variant_id) ? n : n + item.quantity), 0) : undefined;
    }
    document.dispatchEvent(new CartAddEvent({}, variantId, { source: 'xz-v2-collection', itemCount, productId: variantId, sections }));
    if (status) status.textContent = 'Added to cart';
    focusCartDrawer(button);
  } catch (error) {
    window.location.assign(form.dataset.productUrl || form.action);
  } finally {
    delete form.dataset.busy;
    button?.removeAttribute('aria-busy');
  }
}
if (!window.xzV2CardAtcBound) {
  window.xzV2CardAtcBound = true;
  document.addEventListener('click', (event) => {
    if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const button = event.target.closest?.('[data-plp-atc] button[type="submit"]');
    if (!button) return;
    event.preventDefault();
    addCardToCart(button.form || button.closest('[data-plp-atc]'));
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-plp-atc]');
    if (!form) return;
    event.preventDefault();
    addCardToCart(form);
  });
}
